import { Button } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconUpload } from '@tabler/icons-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ImportDialog } from '@/pages/NewAdminConsole/components/ImportDialog';
import { usePermissionControl } from '@/pages/NewAdminConsole/hooks/usePermissionControl';
import { usePermissionData } from '@/pages/NewAdminConsole/hooks/usePermissionData';
import { permissionApi } from '@/pages/NewAdminConsole/services/permissionApi';

interface Props {
  // 任务提交成功后触发，供父组件引导打开任务列表 Drawer
  onSubmitted?: () => void;
}

export const ImportUsers = ({ onSubmitted }: Props) => {
  const { t } = useTranslation();
  const permissionControl = usePermissionControl();
  const { membersData } = usePermissionData();
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [importing, setImporting] = useState(false);

  const onClose = () => {
    setFile(null);
    setImporting(false);
    setShowImportDialog(false);
  };

  const handleAsyncImportConfirm = async () => {
    if (!file) {
      notifications.show({
        message: t('adminConsole.newConsole.import.noFileSelected'),
        color: 'red',
      });
      return;
    }

    setImporting(true);

    try {
      const response = await permissionApi.asyncSimpleImportUsers(file);

      if (!response.success) throw (response as any).error;

      notifications.show({
        message: t('adminConsole.newConsole.import.tasks.submittedHint'),
        color: 'green',
      });
      onClose();
      onSubmitted?.();
    } catch (error: any) {
      console.error('Import failed:', error?.message);
      setFile(null);

      const translationError = t(`adminConsole.tip.${error?.name}`);

      notifications.show({
        message:
          error?.name && translationError !== `adminConsole.tip.${error.name}`
            ? translationError
            : t('adminConsole.newConsole.import.uploadFailed'),
        color: 'red',
      });
    } finally {
      setImporting(false);
    }
  };

  if (!permissionControl.canCreateUser()) return null;

  return (
    <>
      <Button size="sm" leftSection={<IconUpload size={16} />} onClick={() => setShowImportDialog(true)}>
        {t('adminConsole.newConsole.import.title')}
      </Button>
      <ImportDialog
        file={file}
        importing={importing}
        opened={showImportDialog}
        dialogTitle={t('adminConsole.newConsole.import.title')}
        onClose={onClose}
        setFile={setFile}
        onConfirmClicked={handleAsyncImportConfirm}
        currentMaxId={Math.max(...membersData.map((m) => Number(m.id)), 0)}
        confirmButtonText={t('adminConsole.newConsole.import.confirmImport')}
      />
    </>
  );
};
